import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { API_URL } from "../../constants/url";
import AppleIcon from "@mui/icons-material/Apple";
import ShopIcon from "@mui/icons-material/Shop";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import "./Public.scss";

const PUBLIC_API_URL = API_URL.replace(/\/admin\/?$/, "/user/public/");
const APP_STORE_URL = "https://apps.apple.com/app/id6475730302";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.maskan";

type LocalizedEntity = {
  name?: string;
  name_ar?: string;
};

type PublicUser = {
  fullName?: string;
  profilePic?: string;
  image?: string;
  countryCode?: string;
  phone?: string;
};

type PublicPropertyData = {
  _id: string;
  name?: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  price?: number | string;
  totalArea?: number;
  negotiablePrice?: boolean;
  type?: number;
  listingType?: number;
  address?: string;
  mapLink?: string;
  image?: string[];
  thumbnail?: string[];
  categoryId?: LocalizedEntity;
  subCategoryId?: LocalizedEntity;
  areaId?: LocalizedEntity;
  subAreaId?: LocalizedEntity;
  userId?: PublicUser;
  agentAssigned?: PublicUser[];
};

const isVideoUrl = (url: string) =>
  /\.(mp4|mov|m4v|webm)(?:$|\?)/i.test(url) || url.includes("/video/upload/");

const PublicProperty = () => {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isArabic = searchParams.get("lang") === "ar";
  const [property, setProperty] = useState<PublicPropertyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);

    fetch(`${PUBLIC_API_URL}property/${id}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Property request failed with ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data && data.data) {
          setProperty(data.data);
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(true);
        setLoading(false);
      });

    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    document.documentElement.lang = isArabic ? "ar" : "en";
    document.title = property?.name
      ? `${isArabic ? property.name_ar || property.name : property.name} | 4Homes`
      : "4Homes";
  }, [isArabic, property]);

  const images = useMemo(() => {
    if (!property) return [];
    const propertyImages = (property.image || [])
      .map((url) => resolveMediaUrl(url))
      .filter((url): url is string => Boolean(url) && !isVideoUrl(url));
    if (propertyImages.length) return propertyImages;
    return (property.thumbnail || [])
      .map((url) => resolveMediaUrl(url))
      .filter(Boolean);
  }, [property]);

  const text = {
    loading: isArabic ? "جارٍ تحميل العقار..." : "Loading property...",
    notFoundTitle: isArabic ? "هذا العقار غير متاح" : "This property is unavailable",
    notFoundBody: isArabic
      ? "قد يكون الإعلان قد انتهى أو تمت إزالته."
      : "This listing may have expired or been removed.",
    openApp: isArabic ? "افتح في تطبيق 4Homes" : "Open in the 4Homes app",
    share: isArabic ? "مشاركة" : "Share",
    copied: isArabic ? "تم نسخ الرابط" : "Link copied",
    description: isArabic ? "عن هذا العقار" : "About this property",
    listedBy: isArabic ? "مُدرج بواسطة" : "Listed by",
    area: isArabic ? "المساحة" : "Area",
    location: isArabic ? "الموقع" : "Location",
    propertyType: isArabic ? "نوع العقار" : "Property type",
    negotiable: isArabic ? "السعر قابل للتفاوض" : "Price negotiable",
    appTitle: isArabic ? "اعثر على منزلك القادم" : "Find your next home",
    appBody: isArabic
      ? "افتح الإعلان في تطبيق 4Homes للحصول على التجربة الكاملة والتواصل."
      : "Open this listing in 4Homes for the full experience and contact options.",
    image: isArabic ? "صورة العقار" : "Property image",
  };

  if (loading) {
    return (
      <main className="public-container property-page property-state" dir={isArabic ? "rtl" : "ltr"}>
        <div className="property-spinner" aria-hidden="true" />
        <p>{text.loading}</p>
      </main>
    );
  }

  if (error || !property) {
    return (
      <main className="public-container property-page property-state" dir={isArabic ? "rtl" : "ltr"}>
        <img className="brand-logo" src="/static/images/brand-logo.png" alt="4Homes" />
        <h1>{text.notFoundTitle}</h1>
        <p>{text.notFoundBody}</p>
        <a className="primary-action compact" href={isArabic ? "?lang=en" : "?lang=ar"}>
          {isArabic ? "English" : "العربية"}
        </a>
      </main>
    );
  }

  const localizedName = isArabic
    ? property.name_ar || property.name
    : property.name || property.name_ar;
  const localizeEntity = (entity?: LocalizedEntity) =>
    isArabic ? entity?.name_ar || entity?.name : entity?.name || entity?.name_ar;
  const category = localizeEntity(property.subCategoryId) || localizeEntity(property.categoryId);
  const area = localizeEntity(property.subAreaId) || localizeEntity(property.areaId);
  const locationLabel = [area, property.address].filter(Boolean).join(" · ");
  const listingType =
    property.type === 1
      ? isArabic
        ? "للبيع"
        : "For sale"
      : property.type === 2
        ? isArabic
          ? "للإيجار"
          : "For rent"
        : property.type === 3
          ? isArabic
            ? "للتبادل"
            : "For exchange"
          : "";
  const host =
    property.listingType === 3 && property.agentAssigned?.length
      ? property.agentAssigned[0]
      : property.userId;
  const hostImage = resolveMediaUrl(host?.profilePic || host?.image);
  const price =
    property.price !== undefined && property.price !== null
      ? new Intl.NumberFormat("en-KW").format(Number(property.price))
      : null;
  const appLink = /iPad|iPhone|iPod/.test(window.navigator.userAgent)
    ? `homeskw://property/${id}`
    : `maskanapp://property/${id}`;

  const changeImage = (direction: number) => {
    setActiveImage((current) => (current + direction + images.length) % images.length);
  };

  const handleShare = async () => {
    const shareData = {
      title: localizedName || "4Homes",
      text: localizedName,
      url: window.location.href,
    };

    try {
      if (window.navigator.share) {
        await window.navigator.share(shareData);
      } else {
        await window.navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Closing the native share sheet does not require an error state.
    }
  };

  return (
    <main className="public-container property-page" dir={isArabic ? "rtl" : "ltr"}>
      <header className="public-header">
        <a className="public-brand" href="/" aria-label="4Homes">
          <img className="brand-logo brand-logo--header" src="/static/images/brand-logo.png" alt="4Homes" />
        </a>
        <div className="header-actions">
          <a className="language-switch" href={isArabic ? "?lang=en" : "?lang=ar"}>
            {isArabic ? "English" : "العربية"}
          </a>
          <button className="share-button" type="button" onClick={handleShare}>
            {copied ? <ContentCopyRoundedIcon /> : <ShareOutlinedIcon />}
            <span>{copied ? text.copied : text.share}</span>
          </button>
        </div>
      </header>

      <section className="property-shell">
        <div className="property-gallery">
          <div className="gallery-main">
            {images.length ? (
              <img src={images[activeImage]} alt={`${text.image} ${activeImage + 1}`} />
            ) : (
              <div className="gallery-placeholder">
                <img
                  className="brand-logo brand-logo--gallery"
                  src="/static/images/brand-logo-white.png"
                  alt="4Homes"
                />
              </div>
            )}
            {listingType && <span className="listing-badge">{listingType}</span>}
            {images.length > 1 && (
              <>
                <button
                  className="gallery-arrow previous"
                  type="button"
                  aria-label="Previous image"
                  onClick={() => changeImage(-1)}
                >
                  {isArabic ? <ArrowForwardIosRoundedIcon /> : <ArrowBackIosNewRoundedIcon />}
                </button>
                <button
                  className="gallery-arrow next"
                  type="button"
                  aria-label="Next image"
                  onClick={() => changeImage(1)}
                >
                  {isArabic ? <ArrowBackIosNewRoundedIcon /> : <ArrowForwardIosRoundedIcon />}
                </button>
                <span className="image-counter">{activeImage + 1} / {images.length}</span>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="gallery-thumbnails">
              {images.slice(0, 6).map((image, index) => (
                <button
                  className={activeImage === index ? "active" : ""}
                  type="button"
                  key={`${image}-${index}`}
                  onClick={() => setActiveImage(index)}
                  aria-label={`${text.image} ${index + 1}`}
                >
                  <img src={image} alt="" />
                  {index === 5 && images.length > 6 && <span>+{images.length - 6}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="property-layout">
          <article className="property-content">
            <div className="property-heading">
              <div>
                {category && <p className="eyebrow">{category}</p>}
                <h1>{localizedName || category || "4Homes"}</h1>
                {locationLabel && (
                  <p className="property-location">
                    <LocationOnOutlinedIcon />
                    <span>{locationLabel}</span>
                  </p>
                )}
              </div>
              {price && (
                <div className="property-price">
                  <strong>{price}</strong>
                  <span>{isArabic ? "د.ك" : "KWD"}</span>
                </div>
              )}
            </div>

            <div className="property-facts">
              {property.totalArea ? (
                <div>
                  <span className="fact-icon"><StraightenRoundedIcon /></span>
                  <span><small>{text.area}</small><strong>{property.totalArea} {isArabic ? "م²" : "m²"}</strong></span>
                </div>
              ) : null}
              {category ? (
                <div>
                  <span className="fact-icon">⌂</span>
                  <span><small>{text.propertyType}</small><strong>{category}</strong></span>
                </div>
              ) : null}
              {area ? (
                <div>
                  <span className="fact-icon"><LocationOnOutlinedIcon /></span>
                  <span><small>{text.location}</small><strong>{area}</strong></span>
                </div>
              ) : null}
            </div>

            {property.negotiablePrice && (
              <p className="negotiable-label">✓ {text.negotiable}</p>
            )}

            {property.description && (
              <section className="property-description">
                <h2>{text.description}</h2>
                <p>{property.description}</p>
              </section>
            )}

            {host && (
              <section className="property-host">
                <div className="host-avatar">
                  {hostImage ? (
                    <img src={hostImage} alt={host.fullName || text.listedBy} />
                  ) : (
                    <span>{host.fullName?.charAt(0) || "4"}</span>
                  )}
                </div>
                <div>
                  <small>{text.listedBy}</small>
                  <strong>{host.fullName || "4Homes"}</strong>
                </div>
              </section>
            )}
          </article>

          <aside className="app-card">
            <img className="app-card-logo brand-logo" src="/static/images/brand-logo-white.png" alt="4Homes" />
            <h2>{text.appTitle}</h2>
            <p>{text.appBody}</p>
            <a className="primary-action" href={appLink}>
              {text.openApp}
              <OpenInNewRoundedIcon />
            </a>
            <div className="store-divider"><span>{isArabic ? "أو حمّل التطبيق" : "or download the app"}</span></div>
            <div className="store-buttons">
              <a href={APP_STORE_URL} className="store-btn" target="_blank" rel="noopener noreferrer">
                <AppleIcon />
                <span><small>{isArabic ? "تحميل من" : "Download on the"}</small>App Store</span>
              </a>
              <a href={PLAY_STORE_URL} className="store-btn" target="_blank" rel="noopener noreferrer">
                <ShopIcon />
                <span><small>{isArabic ? "احصل عليه من" : "GET IT ON"}</small>Google Play</span>
              </a>
            </div>
          </aside>
        </div>
      </section>

      <footer className="public-footer">
        <span>© {new Date().getFullYear()} 4Homes</span>
        <span>{isArabic ? "عقارات الكويت، ببساطة." : "Kuwait real estate, made simple."}</span>
      </footer>
    </main>
  );
};

export default PublicProperty;
